import { FC, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { TextField, Button, Box, Typography, Divider, MenuItem } from '@mui/material'

import { columns } from '../../consts/consts'
import CustomSelect from './Select'

interface IProps {
  data: boolean
}

const Filters: FC<IProps> = ({ data }) => {
  const [filters, setFilters] = useState<{ [key: string]: string }>({})
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    const filterObj: { [key: string]: string } = {}
    columns.forEach((key) => {
      const filterValue = searchParams.get(key)
      if (filterValue !== null) {
        filterObj[key] = filterValue
      }
    })
    setFilters(filterObj)
  }, [])

  const handleFilterChange = (column: string, value: string) => {
    setFilters((prev) => {
      const updated = { ...prev }
      if (value.trim() === '') {
        delete updated[column]
      } else {
        updated[column] = value
      }
      return updated
    })
  }

  const clearFiltersFromUrl = () => {
    const newParams = new URLSearchParams(searchParams.toString())
    columns.forEach((key) => {
      newParams.delete(key)
    })
    newParams.delete('page')
    setSearchParams(newParams)
  }

  const setFiltersToUrl = (filters: { [key: string]: string }) => {
    const newParams = new URLSearchParams(searchParams.toString())
    columns.forEach((key) => {
      newParams.delete(key)
    })
    Object.entries(filters).forEach(([key, value]) => {
      if (value.trim()) {
        newParams.set(key, value)
      }
    })
    newParams.delete('page')
    setSearchParams(newParams)
  }

  const resetFilters = () => {
    setFilters({})
    clearFiltersFromUrl()
  }

  return (
    <Box
      sx={{
        borderRadius: '14px',
        backgroundColor: '#ffffff',
        p: '14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '14px',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ fontSize: '18px' }} gutterBottom>
          Filters
        </Typography>
        <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <Button
            variant="contained"
            onClick={() => {
              setFiltersToUrl(filters)
            }}
            disabled={!data}
          >
            Apply Filters
          </Button>
          <Button variant="contained" color="primary" onClick={resetFilters} disabled={!data}>
            Reset Filters
          </Button>
        </Box>
      </Box>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '16px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            alignItems: 'center',
          }}
        >
          {columns.map((col) => {
            if (col === 'type') {
              return (
                <CustomSelect
                  key={col}
                  column={col}
                  data={!data}
                  value={filters[col] || ''}
                  setValue={handleFilterChange}
                  label={col[0].toUpperCase() + col.slice(1)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {Array.from({ length: 20 }, (_, i) => i + 1).map((num) => (
                    <MenuItem key={num} value={String(num)}>
                      {num}
                    </MenuItem>
                  ))}
                </CustomSelect>
              )
            } else if (col === 'relevant') {
              return (
                <CustomSelect
                  key={col}
                  column={col}
                  data={!data}
                  value={filters[col] || ''}
                  setValue={handleFilterChange}
                  label={col[0].toUpperCase() + col.slice(1)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  <MenuItem value="true">True</MenuItem>
                  <MenuItem value="false">False</MenuItem>
                </CustomSelect>
              )
            } else
              return (
                <TextField
                  key={col}
                  label={col.split('_').map(a => a[0].toUpperCase() + a.slice(1)).join(' ')}
                  variant="outlined"
                  size="small"
                  value={filters[col] || ''}
                  onChange={(e) => handleFilterChange(col, e.target.value)}
                  disabled={!data}
                  slotProps={{
                    input: {
                      sx: {
                        height: 32,
                        fontSize: '12px',
                      },
                    },
                    inputLabel: {
                      sx: {
                        fontSize: '12px',
                      },
                    },
                  }}
                />
              )
          })}
        </Box>
      </Box>
    </Box>
  )
}

export default Filters
